import useOrderDetail from "@repo/shopify/hooks/useOrderDetail";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import type { IOrdersEdgesNode } from "@repo/shopify/src/queries/customer-gql";
import { extractId } from "@repo/shopify/src/utils";
import { formatOrderAddress } from "@repo/shopify/src/utils/address";
import { calculateLineItems, orderHasRefund } from "@repo/shopify/src/utils/order";
import { transformWon } from "@repo/utils/src/number";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Row from "@components/atoms/Row";

import Loading from "@molecules/Loading";
import OrderLineItem from "@molecules/OrderLineItem";

import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

interface IProps {
  order?: IOrdersEdgesNode;
  /** 카드 정보 표시 여부
   * 일반 주문 및 온라인 드로우 일경우, 카드 정보가 존재함
   * 오프라인 드로우일 경우, 카드정보 존재하지 않음
   */
  isCardInfo?: boolean;
  /** 드로우 페이지 여부 */
  isDrawPage?: boolean;
}

function OrderProductsInfo({ order, isCardInfo = false, isDrawPage = false }: IProps) {
  const { t } = useTranslation();
  const { orderId } = useParams();

  const { customer } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (!order) navigate(URLS.NOT_FOUND);
  }, [order]);

  const { completeOrder, refundOrder, returnOrder, totalReturnShippingFee, isLoading } = useOrderDetail({
    lang: "ko",
    customer,
    handle: orderId,
    order: order!,
  });

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang: "ko", order, orderId: extractId(order?.id || "") });

  // 성수 스토어에서 결제한 건인지 확인
  const isOffline = order?.isOffline?.value;

  const getDiscountCode = (order: IOrdersEdgesNode) => {
    const discountCodeApplication = order?.discountApplications?.edges.find(
      (discount) => discount.node.__typename === "DiscountCodeApplication",
    )?.node;

    return (
      (discountCodeApplication && "code" in discountCodeApplication && discountCodeApplication.code) ||
      // (manualDiscountApplication && "title" in manualDiscountApplication && manualDiscountApplication.title) ||
      null
    );
  };

  const discountCode = order ? getDiscountCode(order) : null;

  /* 소계: 상품의 총 금액 (모든 lineItems의 CompareAtPrice 합한 금액)
   * - 라인아이템의 variant가 하나라도 null 인 경우, 실 결제 금액으로 노출되도록 함
   */
  const originalTotalPrice = order?.lineItems?.edges?.every((lineItem) => lineItem.node.variant)
    ? order?.lineItems?.edges?.reduce((sum, lineItem) => {
        const compareAtPrice = lineItem.node.variant?.compareAtPrice
          ? Number(lineItem.node.variant.compareAtPrice?.amount)
          : null;
        const price = lineItem.node.variant?.price ? Number(lineItem.node.variant.price?.amount) : 0;
        const itemTotal = lineItem.node.quantity * (compareAtPrice ? compareAtPrice : price);
        return sum + itemTotal;
      }, 0)
    : Number(order?.subtotalPrice?.amount ?? "0");

  /* 할인: 소계(상품의 총 금액) - 결제 금액(실 결제 금액)
   * - 라인아이템의 variant가 하나라도 null 인 경우, 결제 당시 할인된 금액으로 노출되도록 함
   */
  const discountedTotalPrice = originalTotalPrice - parseFloat(order?.subtotalPrice?.amount ?? "0");

  const shippingAddress = formatOrderAddress({ address: order?.shippingAddress, lang: "ko" });

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* 주문 진행 내역 - (unfulfilled, fulfilled, onhold) */}
          {completeOrder.length > 0 ? (
            <section className="flex flex-col gap-5">
              <span>{!isDrawPage ? "주문 내역" : "응모 내역"}</span>
              <ul className="flex flex-col gap-5">
                {completeOrder.map((lineItem) => {
                  const { compareAtPrice, totalDiscountedPrice } = calculateLineItems(lineItem);

                  return (
                    <OrderLineItem
                      compareAtPrice={compareAtPrice.toString()}
                      discountedTotalPrice={totalDiscountedPrice.toString()}
                      handle={lineItem.variant?.product?.handle ?? ""}
                      image={"image" in lineItem ? (lineItem.image ?? null) : (lineItem.variant?.image ?? null)}
                      key={lineItem.id}
                      originalTotalPrice={
                        lineItem.originalTotalPrice ? (lineItem.originalTotalPrice.amount ?? "0") : "0"
                      }
                      quantity={lineItem.quantity}
                      size={
                        lineItem.variant
                          ? lineItem.variant.selectedOptions.find((option) => option.name === "Size")?.value || ""
                          : (/\/\s*(?:.*)/.exec(("variantTitle" in lineItem && lineItem.variantTitle) || "") || [])[1]
                      }
                      tags={lineItem.variant?.product?.tags ?? null}
                      title={lineItem.title}
                      vendor={lineItem.variant?.product?.vendor ?? ""}
                    />
                  );
                })}
              </ul>
            </section>
          ) : null}

          {/* 반품 내역 - (returnRequested, returnApproved) */}
          {returnOrder.length > 0 ? (
            <section className="flex flex-col gap-5">
              <span>{t("account.return_history")}</span>
              <ul className="flex flex-col gap-5">
                {returnOrder.map((lineItem) => {
                  const { compareAtPrice, totalDiscountedPrice } = calculateLineItems(lineItem);

                  return (
                    <OrderLineItem
                      compareAtPrice={compareAtPrice.toString()}
                      discountedTotalPrice={totalDiscountedPrice.toString()}
                      handle={lineItem.variant?.product?.handle ?? ""}
                      image={"image" in lineItem ? (lineItem.image ?? null) : (lineItem.variant?.image ?? null)}
                      key={lineItem.id}
                      originalTotalPrice={
                        lineItem.originalTotalPrice ? (lineItem.originalTotalPrice.amount ?? "0") : "0"
                      }
                      quantity={lineItem.quantity}
                      returnReason={lineItem.returnReason}
                      size={
                        lineItem.variant
                          ? lineItem.variant.selectedOptions.find((option) => option.name === "Size")?.value || ""
                          : (/\/\s*(?:.*)/.exec(("variantTitle" in lineItem && lineItem.variantTitle) || "") || [])[1]
                      }
                      tags={lineItem.variant?.product?.tags ?? null}
                      title={lineItem.title}
                      vendor={lineItem.variant?.product?.vendor ?? ""}
                    />
                  );
                })}
              </ul>
            </section>
          ) : null}

          {/* 환불 내역 - (canceled, refunded)*/}
          {refundOrder.length > 0 ? (
            <section className="flex flex-col gap-5">
              <span>환불 내역</span>
              <ul className="flex flex-col gap-5">
                {refundOrder.map((lineItem) => {
                  const { compareAtPrice, totalDiscountedPrice } = calculateLineItems(lineItem);

                  return (
                    <OrderLineItem
                      compareAtPrice={compareAtPrice.toString()}
                      discountedTotalPrice={totalDiscountedPrice.toString()}
                      handle={lineItem.variant?.product?.handle ?? ""}
                      image={"image" in lineItem ? (lineItem.image ?? null) : (lineItem.variant?.image ?? null)}
                      key={lineItem.id}
                      originalTotalPrice={
                        lineItem.originalTotalPrice ? (lineItem.originalTotalPrice.amount ?? "0") : "0"
                      }
                      quantity={lineItem.quantity}
                      size={
                        lineItem.variant
                          ? lineItem.variant.selectedOptions.find((option) => option.name === "Size")?.value || ""
                          : (/\/\s*(\d+)/.exec(("variantTitle" in lineItem && lineItem.variantTitle) || "") || [])[1]
                      }
                      tags={lineItem.variant?.product?.tags ?? null}
                      title={lineItem.title}
                      vendor={lineItem.variant?.product?.vendor ?? ""}
                    />
                  );
                })}
              </ul>
            </section>
          ) : null}

          {/* `Draw 페이지의 신청 내역` */}
          {isDrawPage ? (
            <section className="flex flex-col gap-1">
              <div className="flex w-full justify-between">
                <div>{t("account.order.instagram")}</div>{" "}
                <span>{order?.instagram?.value.replace("Instagram:", "") ?? ""}</span>
              </div>
              <div className="flex w-full justify-between">
                <div>{t("account.order.receive")}</div>
                <span>{order?.shippingAddress ? t("account.order.delivery") : t("account.order.pickup")}</span>
              </div>
              {isCardInfo ? (
                <div className="flex w-full justify-between">
                  <div>{t("account.draw.auto_pay")}</div>
                  <span>{isOffline ? "성수 스토어 결제" : paymentInfo}</span>
                </div>
              ) : null}
            </section>
          ) : null}

          {/* `Order Detail 페이지의 OrderSummary 영역 `*/}
          {!isDrawPage ? (
            <section className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                {/* 소계: 주문의 총 금액 - 상품의 compareAtPrice 합한 금액, variant가 null인 경우는 실 결제 금액이 노출됨 */}
                <Row left={t("account.order.total")} right={`${transformWon(originalTotalPrice)}원`} />
                {/* 배송비 : 오프라인 구매가 아닌경우만 노출 */}
                {!isOffline && (
                  <Row
                    left={t("account.order.charge")}
                    right={
                      parseInt(order?.totalShippingPrice?.amount || "0") === 0
                        ? t("account.order.free")
                        : `${transformWon(parseInt(order?.totalShippingPrice?.amount || "0"))}원`
                    }
                  />
                )}
                {/* 할인: 총 할인된 금액 - 상품할인, 멤버십 상시 할인, 할인코드 할인 */}
                {discountedTotalPrice > 0 ? (
                  <Row
                    left={t("account.order.discount")}
                    right={`${discountedTotalPrice > 0 ? "-" : ""}${transformWon(discountedTotalPrice)}원`}
                  />
                ) : null}
                {/* 할인코드 : 할인코드가 존재할 경우만 노출 */}
                {discountCode ? <Row left={t("account.order.discount_code")} right={discountCode} /> : null}
              </div>
              <div className="flex flex-col gap-1">
                {/* 결제 수단 */}
                <Row left={t("account.order.payment_method")} right={isOffline ? "성수 스토어 결제" : paymentInfo} />
                {/* 결제 금액: 소계 + 배송비 - 할인 */}
                <Row
                  left={t("account.order.payment")}
                  right={isOffline ? "" : `${transformWon(parseInt(order?.totalPrice?.amount || "0"))}원`}
                />
                {/* 환불 금액 */}
                {orderHasRefund(order) && (
                  <Row left="환불 금액" right={`-${transformWon(parseInt(order?.totalRefunded.amount || "0"))}원`} />
                )}
              </div>
              <div className="flex flex-col gap-1">
                {/* 반품 배송비 */}
                {orderHasRefund(order) && totalReturnShippingFee ? (
                  <Row left="반품 배송비" right={`${transformWon(totalReturnShippingFee || 0)}원`} />
                ) : null}
              </div>
            </section>
          ) : null}

          {/* 배송지 영역 */}
          {order?.shippingAddress ? (
            <div className="flex gap-5">
              <div className="w-1/2">
                <div className="mb-2.5">{t("account.order.address")}</div>
                <p>
                  {shippingAddress.name}
                  <br />
                  {shippingAddress.province}
                  <br />
                  {shippingAddress.address1}
                  <br />
                  {shippingAddress.address2}
                  <br />
                  {shippingAddress.phone}
                </p>
              </div>
              <div className="w-1/2">
                <div className="mb-2.5">{t("account.order.billing")}</div>
                <p>
                  {shippingAddress.name}
                  <br />
                  {shippingAddress.province}
                  <br />
                  {shippingAddress.address1}
                  <br />
                  {shippingAddress.address2}
                  <br />
                  {shippingAddress.phone}
                </p>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

export default OrderProductsInfo;
